<script lang="ts" module>
  import "simplebar"
  import "simplebar/dist/simplebar.css"
</script>

<script lang="ts">
  import { containOverlayFocus } from "./_logic/contain-focus"
  import { mdiCloseCircleOutline, mdiFullscreen, mdiFullscreenExit } from "@mdi/js"
  import type { Snippet } from "svelte"
  import Icon from "../../../primitives/_modules/icon/index"
  import {
    overlays,
    isMobile,
    changeStateOfSite,
    requestCloseOverlaysFromIndex,
    setOverlayCloseDisabled,
  } from "../../../../core/overlay/globalStores"

  const resolveIsDark = (overlay: { isDark?: boolean }, index: number) =>
    overlay.isDark === undefined ? (index + 1) % 2 === 1 : overlay.isDark

  const overlayVisible = $derived($overlays.length > 0)
  $effect(() => {
    changeStateOfSite(overlayVisible)
  })

  let dispatched = $state<boolean[]>([])
  let isFullscreen = $state(false)
  let fullscreenSeedOverlayId = $state<string | null>(null)
  let actionButtonSnippets = $state<Record<string, Snippet | null>>({})

  const visibleOverlayCount = $derived(
    $overlays.reduce(
      (count, entry, index) => count + (entry.hideTillDispatch && !dispatched[index] ? 0 : 1),
      0,
    ),
  )
  const visibleOverlayIndices = $derived(
    $overlays
      .map((overlay, index) => ({ overlay, index }))
      .filter(({ overlay, index }) => !(overlay.hideTillDispatch && !dispatched[index]))
      .map(({ index }) => index),
  )
  const topVisibleOverlayIndex = $derived(
    visibleOverlayIndices.length ? visibleOverlayIndices[visibleOverlayIndices.length - 1] : -1,
  )
  const hasMultipleVisibleOverlays = $derived(visibleOverlayIndices.length > 1)
  const collapsedOverlayIndices = $derived.by(() => {
    if ($isMobile) return []

    if (visibleOverlayIndices.length <= 2) return []

    const newest = visibleOverlayIndices[visibleOverlayIndices.length - 1]
    const secondNewest = visibleOverlayIndices[visibleOverlayIndices.length - 2]
    const activeIndex = visibleOverlayIndices.find((index) => $overlays[index]?.active) ?? -1

    const expanded = [newest]

    if (activeIndex !== -1 && activeIndex !== newest) {
      expanded.push(activeIndex)
    } else if (secondNewest !== undefined) {
      expanded.push(secondNewest)
    }

    return visibleOverlayIndices.filter((index) => !expanded.includes(index))
  })
  const showFullscreenToggle = $derived(visibleOverlayCount === 1 && !$isMobile)
  const effectiveFullscreen = $derived(showFullscreenToggle && isFullscreen)
  const activeOverlay = $derived($overlays.find((entry) => entry.active) ?? null)
  const effectiveMobileFullscreen = $derived(
    Boolean($isMobile && activeOverlay?.defaultMobileFullscreen),
  )
  const effectiveOverlayFullscreen = $derived(effectiveFullscreen || effectiveMobileFullscreen)
  const visibleActionButtonSnippets = $derived.by(() => {
    const activeIds = new Set($overlays.map((entry) => entry.id))
    const next: Record<string, Snippet | null> = {}
    Object.entries(actionButtonSnippets).forEach(([id, snippet]) => {
      if (activeIds.has(id)) next[id] = snippet
    })

    return next
  })

  $effect(() => {
    const activeId = activeOverlay?.id ?? null

    if (!activeId) {
      isFullscreen = false
      fullscreenSeedOverlayId = null

      return
    }

    if (fullscreenSeedOverlayId === activeId) return

    fullscreenSeedOverlayId = activeId
    isFullscreen = Boolean(activeOverlay?.defaultFullscreen)
  })

  const setActiveOverlay = (index: number) => {
    overlays.update((list) =>
      list.map((entry, position) => ({ ...entry, active: position === index })),
    )
  }
  const ensureActiveOverlay = () => {
    if (!$overlays.length) return

    if ($overlays.some((entry) => entry.active)) return

    if ($overlays.some((entry) => entry.hideTillDispatch)) {
      const hidden = $overlays.findIndex((entry) => entry.hideTillDispatch)

      if (hidden > 0) setActiveOverlay(hidden - 1)
      else if ($overlays.length > 1) setActiveOverlay(1)
      else setActiveOverlay(0)
    } else {
      const lastIndex = $overlays.length - 1

      if (lastIndex >= 0) setActiveOverlay(lastIndex)
    }
  }

  $effect(() => {
    ensureActiveOverlay()
  })

  const closeModal = async (index: number, force = false) => {
    const overlayId = $overlays[index]?.id

    if (!force && $overlays.slice(Math.max(0, index)).some((entry) => entry.closeDisabled)) return

    const closingIds = $overlays.slice(Math.max(0, index)).map((entry) => entry.id)

    if (!closingIds.length) return

    if (!(await requestCloseOverlaysFromIndex(index, force))) return

    dispatched = dispatched.slice(0, Math.max(0, index))

    if (overlayId) {
      const next = { ...actionButtonSnippets }
      for (const id of closingIds) {
        delete next[id]
      }
      actionButtonSnippets = next
    }
  }

  const closeTopVisibleOverlay = () => {
    if (topVisibleOverlayIndex < 0) return

    closeModal(topVisibleOverlayIndex)
  }

  const handleShowOverlay = (index: number) => {
    if (!dispatched[index]) {
      dispatched[index] = true
    }
  }

  const setActionButtonsForOverlay =
    (overlayId: string) =>
    (snippet: Snippet | null = null) => {
      actionButtonSnippets = { ...actionButtonSnippets, [overlayId]: snippet }
    }

  let overlayRoot: HTMLDivElement | null = $state(null)
  $effect(() => {
    if (!overlayRoot) return

    if (!hasMultipleVisibleOverlays) return

    const active = document.activeElement as HTMLElement | null

    if (!active) return

    const blocked = active.closest?.(".content-block.blocked")

    if (!blocked) return

    overlayRoot.focus()
  })
</script>

<div
  class="sidebarOverlay"
  class:visible={$overlays.length}
  bind:this={overlayRoot}
  id="overlay"
  role="dialog"
  tabindex="-1"
  aria-labelledby="overlayTitle"
  aria-label="Overlay"
  aria-modal="true"
  onclick={closeTopVisibleOverlay}
  onkeydown={(event) => {
    if (event.key !== "Escape" || event.defaultPrevented || topVisibleOverlayIndex < 0) return

    if (event.target instanceof Element && event.target.closest("dialog[open]")) return

    // A close guard may open a native confirmation during this key event.
    event.preventDefault()
    event.stopPropagation()
    closeTopVisibleOverlay()
  }}
>
  <section class="dark-side" role="presentation"></section>
  <div
    class="content-side"
    class:mobileFullscreen={effectiveMobileFullscreen}
    class:fullscreen={effectiveFullscreen}
  >
    {#each $overlays as overlay, i (overlay.id)}
      {@const isDark = resolveIsDark(overlay, i)}
      {@const visiblePosition = visibleOverlayIndices.indexOf(i)}
      {@const isVisible = visiblePosition !== -1}
      {@const isBlocked = hasMultipleVisibleOverlays && isVisible && i !== topVisibleOverlayIndex}
      {@const leftNeighborIndex =
        visiblePosition === -1 ? undefined : visibleOverlayIndices[visiblePosition + 1]}
      {@const leftNeighbor = leftNeighborIndex === undefined ? null : $overlays[leftNeighborIndex]}
      {@const hasLeftNeighbor = Boolean(leftNeighborIndex !== undefined && leftNeighbor)}
      {@const leftNeighborIsDark =
        leftNeighborIndex === undefined || !leftNeighbor
          ? false
          : resolveIsDark(leftNeighbor, leftNeighborIndex)}
      {@const darkOnDark = hasLeftNeighbor && isDark && leftNeighborIsDark}
      {@const lightOnLight = hasLeftNeighbor && !isDark && !leftNeighborIsDark}
      {@const crinkleFill = isDark ? "var(--bg-100)" : "var(--neutral-white)"}
      <section
        class="content-block"
        tabindex="-1"
        use:containOverlayFocus={isVisible && !isBlocked && overlay.active === true}
        class:active={overlay.active}
        class:dark={isDark}
        class:edgeBody={overlay.bodyPadding === "none"}
        class:recordBody={overlay.layout === "record"}
        class:darkOnDark
        class:lightOnLight
        class:blocked={isBlocked}
        class:hideTillDispatch={overlay.hideTillDispatch}
        class:hideIt={!dispatched[i]}
        class:collapsed={collapsedOverlayIndices.includes(i)}
        class:fullscreen={effectiveOverlayFullscreen && overlay.active}
        aria-hidden={isBlocked ? "true" : undefined}
        inert={isBlocked}
        data-overlay-index={i}
        onclick={(event) => event.stopPropagation()}
      >
        {#if isBlocked}
          <div class="interaction-shield" aria-hidden="true"></div>
        {/if}
        <header
          role="button"
          tabindex="0"
          onclick={() => setActiveOverlay(i)}
          onkeydown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              setActiveOverlay(i)
            }
          }}
        >
          <div
            class="upper-bar"
            class:noBg={i === $overlays.length - 1 ||
              $overlays.filter((entry) => entry.hideTillDispatch).length >
                dispatched.filter(Boolean).length}
          >
            <div class="crinkle desktop-crinkle">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="90"
                height="88"
                viewBox="0 0 90 88"
                fill="none"
              >
                <path d="M89.2394 88V0L0 88H89.2394Z" fill={crinkleFill}></path>
                <path class="crinkle-divider" d="M89.2394 0L0 88" fill="none"></path>
              </svg>
            </div>
            <div class="crinkle mobile-crinkle">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="38"
                height="70"
                viewBox="0 0 38 70"
                fill="none"
              >
                <path d="M38.2384 70V0L0 70H38.2384Z" fill={crinkleFill}></path>
                <path class="crinkle-divider" d="M38.2384 0L0 70" fill="none"></path>
              </svg>
            </div>
            {#if typeof overlay.title === "string"}
              <h2 id="overlayTitle">
                {overlay.title.length > 45 ? overlay.title.slice(0, 42) + "..." : overlay.title}
              </h2>
            {:else if typeof overlay.title === "function"}
              {@const TitleComponent = overlay.title}
              <TitleComponent />
            {:else}
              <h2 id="overlayTitle">Dialog</h2>
            {/if}
            <div class="close">
              {#if showFullscreenToggle}
                <button
                  type="button"
                  aria-label={effectiveFullscreen ? "Fenster verkleinern" : "Vollbild"}
                  onclick={(event) => {
                    event.stopPropagation()
                    isFullscreen = !effectiveFullscreen
                  }}
                  class="fullscreen-btn"
                >
                  <Icon
                    path={effectiveFullscreen ? mdiFullscreenExit : mdiFullscreen}
                    size={$isMobile ? "24px" : "32px"}
                  />
                </button>
              {/if}
              <button
                type="button"
                aria-label={overlay.closeDisabled
                  ? "Dialog kann während der Verarbeitung nicht geschlossen werden"
                  : "Close"}
                disabled={overlay.closeDisabled}
                onclick={(event) => {
                  event.stopPropagation()
                  closeModal(i)
                }}
                class="close-btn"
              >
                <Icon path={mdiCloseCircleOutline} size={$isMobile ? "24px" : "32px"} />
              </button>
            </div>
            <div></div>
          </div>
          <div class="lower-bar"></div>
        </header>
        <div class="scroll-container" class:active={overlay.active} data-simplebar>
          <div class="content-listing">
            {#key overlay.reload ?? overlay.id}
              {@const ContentComponent = overlay.content}
              {@const overlayId = overlay.id}
              <ContentComponent
                onshowOverlay={() => handleShowOverlay(i)}
                onremoveOverlay={() => closeModal(i, true)}
                setActionButtonSnippet={setActionButtonsForOverlay(overlayId)}
                {...overlay.properties ?? {}}
                setCloseDisabled={(disabled: boolean) =>
                  setOverlayCloseDisabled(overlayId, disabled)}
              />
            {/key}
          </div>
        </div>
        {#if visibleActionButtonSnippets[overlay.id]}
          <div class="overlay-action-footer">
            <div class="dialog-actions">
              {@render visibleActionButtonSnippets[overlay.id]!()}
            </div>
          </div>
        {/if}
      </section>
    {/each}
  </div>
</div>

<style lang="less">
  @import "../../../../theme/tokens.less";

  :global .sidebarOverlay {
    position: fixed;
    left: 0;
    right: 0;
    width: 100vw;
    height: 100%;
    top: 0;
    overflow: hidden;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: flex-end;
    bottom: -200%;
    top: 200%;
    transition: all 0.25s ease;
    // Prevent hidden overlays from swallowing taps/swipes (iOS Safari can be
    // especially sensitive to off-screen fixed layers).
    pointer-events: none;

    &.visible {
      bottom: 0;
      top: 0;
      left: 0;
      pointer-events: auto;
    }

    @media @desktop {
      top: 0;
      bottom: 0;
      right: -100vw;
      left: 100vw;

      &.visible {
        right: 0;
      }
    }

    .dark-side {
      flex-grow: 1;
      height: 100%;
    }

    .content-side {
      width: 100vw;
      display: flex;
      align-items: flex-end;
      flex-direction: column-reverse;
      height: 100%;

      @media @desktop {
        flex-direction: row-reverse;
      }

      @media @mobile {
        height: 70%;
      }

      &.mobileFullscreen,
      &.fullscreen {
        @media @mobile {
          height: 100%;
        }
      }

      .content-block {
        height: 100%;
        width: 100%;
        min-height: 0;
        display: flex;
        flex-direction: column;
        position: relative;
        @media @mobile {
          height: unset;
        }
        &.hideTillDispatch.hideIt {
          display: none;
        }

        header {
          .upper-bar {
            width: 100%;
            display: flex;
            height: 88px;
            background-color: var(--bg-100);

            &.noBg {
              background-color: transparent;
            }

            .crinkle {
              height: 100%;
              width: auto;
              margin-right: -1px;

              .crinkle-divider {
                stroke: transparent;
                stroke-width: 1px;
                vector-effect: non-scaling-stroke;
                shape-rendering: crispEdges;
              }
            }

            .mobile-crinkle {
              display: none;
            }

            @media @mobile {
              height: 70px;

              .desktop-crinkle {
                display: none;
              }

              .mobile-crinkle {
                display: block;
              }
            }

            h2 {
              background-color: var(--neutral-white);
              flex-grow: 1;
              color: var(--text-invers-100);
              display: flex;
              align-items: flex-end;
              font-size: 22px;
              font-weight: 700;
              line-height: 1.3;

              @media (max-width: 767px) {
                font-size: 19px;
              }
            }

            .close {
              background-color: var(--neutral-white);
              padding-top: 0.6rem;
              padding-right: 27px;
              color: var(--text-invers-100);
              display: flex;
              align-items: flex-end;
              gap: 0.6rem;

              button {
                color: var(--text-invers-100);
                border: none;
                background: transparent;
                cursor: pointer;
              }

              .close-btn {
                transform: none;
              }
            }
          }

          .lower-bar {
            height: 1.2rem;
            background-color: white;
            width: 100%;
          }
        }

        &.active {
          @media @mobile {
            height: unset;
            max-height: calc(100vh - 200px);
            flex-grow: 1;
          }
        }

        .simplebar-content {
          height: 100%;

          @media @mobile {
            padding-top: 12px !important;
          }
        }

        .simplebar-track {
          @media @mobile {
            margin-top: 12px !important;
            margin-bottom: 12px;
          }
        }

        &:only-child {
          height: 100%;
        }

        @media @desktop {
          width: 50vw;
          border-left: 1px solid var(--bg-invers-100, var(--bg-100));
        }

        .interaction-shield {
          position: absolute;
          inset: 0;
          z-index: 10;
          background: transparent;
        }

        &.darkOnDark {
          @media @desktop {
            border-left: 1px solid var(--neutral-white);
          }

          header {
            .upper-bar {
              .crinkle {
                background-color: var(--bg-100);
                position: relative;

                &::before {
                  content: "";
                  position: absolute;
                  top: 0;
                  bottom: 0;
                  left: -2px;
                  width: 2px;
                  background-color: var(--bg-100);
                  pointer-events: none;
                }

                .crinkle-divider {
                  stroke: var(--neutral-white);
                }
              }
            }
          }
        }

        &.lightOnLight {
          @media @desktop {
            border-left: 1px solid var(--bg-invers-100, var(--bg-100));

            &::before {
              content: "";
              position: absolute;
              top: 88px;
              bottom: 0;
              left: 0;
              z-index: 2;
              width: 1px;
              background-color: var(--bg-invers-100, var(--bg-100));
              pointer-events: none;
            }
          }

          header {
            .upper-bar {
              .crinkle {
                background-color: var(--neutral-white);
                position: relative;

                &::before {
                  content: "";
                  position: absolute;
                  top: 0;
                  bottom: 0;
                  left: -2px;
                  width: 2px;
                  background-color: var(--neutral-white);
                  pointer-events: none;
                }

                .crinkle-divider {
                  stroke: var(--bg-invers-100, var(--bg-100));
                }
              }
            }
          }
        }

        @media @large_desktop {
          width: max(40vw, 828px);
        }

        &.fullscreen {
          @media @desktop {
            width: 100vw;
            max-width: 100vw;
            border-left: none;
          }

          @media @mobile {
            height: 100%;
            max-height: 100%;
            flex-grow: 1;
          }
        }

        &.collapsed {
          @media @desktop {
            width: 88px;
            min-width: 88px;
            max-width: 88px;
            flex: 0 0 88px;
            border-left: 1px solid var(--bg-invers-100, var(--bg-100));

            &.darkOnDark {
              border-left: 1px solid var(--neutral-white);
            }

            overflow: hidden;
            background-color: var(--neutral-white);

            header {
              .upper-bar {
                justify-content: flex-start;
              }

              h2,
              .close {
                display: none;
              }

              .lower-bar {
                display: none;
              }
            }

            .scroll-container {
              display: none;
            }

            .overlay-action-footer {
              display: none;
            }
          }
        }

        .scroll-container {
          flex: 1 1 auto;
          min-height: 0;
          padding-right: 1.6rem;
          transition: height 0.3s;
          background-color: var(--neutral-white);
          padding-top: 2.4rem;

          @media @mobile {
            &:not(.active) {
              height: 0;
              max-height: 0;
              padding-top: 0;
              overflow: hidden;
            }
          }

          .simplebar-track {
            background-color: rgba(13, 12, 12, 0.25);
            width: 7px;
            overflow: visible;
            margin-left: 5px;
            margin-right: 1.6rem;
            margin-top: 2.4rem;

            @media @mobile {
              margin-right: 1rem;
            }
          }

          .simplebar-scrollbar {
            transition-duration: 0ms !important;
            cursor: pointer;

            &::before {
              background-color: var(--bg-100);
              top: -2px;
              left: -2px;
              opacity: 1;
              border-radius: 0;
              width: 11px;
              height: calc(100% + 2px);
              transition-delay: 0s;
            }
          }
        }

        .content-listing {
          width: 100%;
          box-sizing: border-box;
          background-color: var(--neutral-white);
          display: flex;
          padding-left: 88px;
          padding-right: 0.4rem;
          @media @mobile {
            padding-left: 38px;
            padding-right: 0.4rem;
          }
          flex-direction: column;
          flex-grow: 1;
        }

        &.edgeBody {
          --progress-content-padding-top: 2.4rem;
          --progress-content-padding-left: 88px;
          --progress-content-padding-right: 2rem;
          --progress-content-padding-bottom: 0px;

          @media @mobile {
            --progress-content-padding-left: 38px;
            --progress-content-padding-right: 1.4rem;
          }

          header {
            flex: 0 0 auto;

            &:focus-visible {
              outline: 2px solid var(--primary-200);
              outline-offset: -2px;
            }

            .upper-bar h2,
            .upper-bar .close {
              align-items: center;
            }

            .upper-bar .close {
              padding-top: 0;
            }

            .lower-bar {
              height: 0;
            }
          }
          .scroll-container {
            padding: 0;
          }
          .content-listing {
            padding: 0;
            min-width: 0;
          }
          .simplebar-content {
            padding-top: 0 !important;
          }
          .simplebar-track {
            margin-top: 0 !important;
            margin-right: 0;
          }
        }

        .overlay-action-footer {
          position: sticky;
          bottom: 0;
          z-index: 1;
          padding-top: 0.4rem;
          padding-left: 88px;
          padding-right: 1.6rem;
          padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1.2rem);
          background-color: var(--neutral-white);

          @media @mobile {
            padding-left: 38px;
            padding-right: 1rem;
          }

          .dialog-actions {
            display: flex;
            justify-content: flex-start;
            gap: 0.8rem;
            margin-top: 1.2rem;
            padding-top: 0.8rem;
            border-top: 1px solid color-mix(in srgb, var(--text-invers-100) 14%, transparent);
          }
        }

        &.dark {
          p:not(.record-header--overlay p):not(.field-editor-popover p) {
            color: var(--text-100) !important;
          }
          @media @desktop {
            &.collapsed {
              background-color: var(--bg-100);
            }
          }

          header {
            .upper-bar {
              background-color: var(--neutral-white);

              &.noBg {
                background-color: transparent;
              }

              h2 {
                background-color: var(--bg-100);
                color: var(--text-100);
              }

              .close {
                background-color: var(--bg-100);

                button {
                  color: var(--text-100) !important;
                  transform: translate(0, 25%);

                  @media @mobile {
                    transform: translate(-50%, 25%);
                  }

                  svg {
                    fill: var(--text-100);
                  }
                }

                .close-btn {
                  transform: translate(25%, 25%);

                  @media @mobile {
                    transform: translate(-50%, 25%);
                  }
                }
              }
            }

            .lower-bar {
              background-color: var(--bg-100);
            }
          }

          .scroll-container {
            background-color: var(--bg-100);

            .simplebar-track {
              background-color: rgba(255, 255, 255, 0.2);
            }

            .simplebar-scrollbar::before {
              background-color: white;
            }
          }

          .content-listing {
            background-color: var(--bg-100);
            color: var(--text-100);
          }

          .overlay-action-footer {
            background-color: var(--bg-100);

            .dialog-actions {
              border-top: 1px solid color-mix(in srgb, var(--text-100) 18%, transparent);
            }
          }
        }
        &.recordBody:not(.collapsed) {
          --kw-record-overlay-inset: 24px;
          @media (max-width: 767px) {
            --kw-record-overlay-inset: 16px;
          }
          > header {
            flex: 0 0 auto;
            .upper-bar {
              height: 52px;
              background: var(--neutral-white);
              padding-left: var(--kw-record-overlay-inset);
              box-sizing: border-box;
              .crinkle {
                display: none;
              }
              h2 {
                margin: 0;
                font-size: 16px;
                letter-spacing: 0;
                align-items: center;
              }
              .close {
                padding: 0 var(--kw-record-overlay-inset) 0 12px;
                align-items: center;
                gap: 4px;
                button,
                button.close-btn {
                  transform: none;
                  width: 36px;
                  height: 44px;
                  padding: 0;
                }
                button svg {
                  width: 22px;
                  height: 22px;
                }
              }
            }
            .lower-bar {
              display: none;
            }
          }
          &.dark > header .upper-bar {
            background: var(--bg-100);
          }
          .scroll-container,
          .content-listing {
            padding: 0;
          }
          .record-overlay-content {
            padding: 20px var(--kw-record-overlay-inset);
            box-sizing: border-box;
            min-width: 0;
          }
          .overlay-action-footer {
            padding-inline: var(--kw-record-overlay-inset);
            padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 16px);
          }
        }
      }
    }
  }
</style>
